import { useState, useCallback, useRef, useEffect } from "react";
import { ChatMessage } from "@/types";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

const WELCOME_MESSAGE: ChatMessage = {
  id: "welcome",
  content: "Hey, I'm here. What's on your mind today? You can share anything, big or small.",
  isUser: false,
  timestamp: new Date(),
};

const CHAT_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/mindful-chat`;

type AiMsg = { role: "user" | "assistant"; content: string };

interface StoredConversation {
  messages: { id: string; content: string; isUser: boolean; timestamp: string; moodCheck?: boolean }[];
  history: AiMsg[];
}

export interface MindfulChatPersistence {
  enabled: boolean;
  saveConversation: (payload: {
    id?: string;
    messages: unknown;
    title: string;
    messageCount: number;
  }) => Promise<string | null>;
  loadConversation: (id: string) => Promise<unknown | null>;
}

export function useMindfulChat(persistence?: MindfulChatPersistence) {
  const [messages, setMessages] = useState<ChatMessage[]>([WELCOME_MESSAGE]);
  const [isTyping, setIsTyping] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const historyRef = useRef<AiMsg[]>([]);
  const messagesRef = useRef<ChatMessage[]>([WELCOME_MESSAGE]);
  const conversationIdRef = useRef<string | null>(null);
  const busyRef = useRef(false);
  const persistenceRef = useRef(persistence);
  const { toast } = useToast();

  useEffect(() => {
    persistenceRef.current = persistence;
  }, [persistence]);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  const persist = useCallback(async () => {
    const p = persistenceRef.current;
    if (!p?.enabled) return;
    const msgs = messagesRef.current;
    const firstUser = msgs.find((m) => m.isUser)?.content ?? "";
    if (!firstUser) return;
    const title = firstUser.length > 60 ? `${firstUser.slice(0, 57)}...` : firstUser;
    const payload: StoredConversation = {
      messages: msgs.map((m) => ({
        id: m.id,
        content: m.content,
        isUser: m.isUser,
        timestamp: m.timestamp.toISOString(),
        moodCheck: m.moodCheck,
      })),
      history: historyRef.current,
    };
    const id = await p.saveConversation({
      id: conversationIdRef.current ?? undefined,
      messages: payload,
      title,
      messageCount: msgs.length,
    });
    if (id && !conversationIdRef.current) {
      conversationIdRef.current = id;
      setConversationId(id);
    }
  }, []);

  const sendMessage = useCallback(async (input: string) => {
    if (!input.trim() || busyRef.current) return;
    busyRef.current = true;

    const userMsg: ChatMessage = {
      id: crypto.randomUUID(),
      content: input,
      isUser: true,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, userMsg]);
    historyRef.current.push({ role: "user", content: input });

    setIsTyping(true);
    let assistantContent = "";

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData.session?.access_token ?? import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
      const resp = await fetch(CHAT_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
          apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
        },
        body: JSON.stringify({ messages: historyRef.current }),
      });

      if (!resp.ok) {
        const err = await resp.json().catch(() => ({ error: "Something went wrong" }));
        toast({ title: "Error", description: err.error || "Failed to get response", variant: "destructive" });
        setIsTyping(false);
        return;
      }

      const reader = resp.body?.getReader();
      if (!reader) throw new Error("No stream");

      const decoder = new TextDecoder();
      let buffer = "";
      // We'll accumulate the full response, then split into multiple bubbles at the end
      const tempId = crypto.randomUUID();

      // Add a temporary streaming message
      setMessages((prev) => [
        ...prev,
        { id: tempId, content: "", isUser: false, timestamp: new Date() },
      ]);

      let streamDone = false;
      while (!streamDone) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        let idx: number;
        while ((idx = buffer.indexOf("\n")) !== -1) {
          let line = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 1);
          if (line.endsWith("\r")) line = line.slice(0, -1);
          if (line.startsWith(":") || line.trim() === "") continue;
          if (!line.startsWith("data: ")) continue;
          const json = line.slice(6).trim();
          if (json === "[DONE]") { streamDone = true; break; }
          try {
            const parsed = JSON.parse(json);
            const delta = parsed.choices?.[0]?.delta?.content as string | undefined;
            if (delta) {
              assistantContent += delta;
              const snap = assistantContent;
              setMessages((prev) =>
                prev.map((m) => (m.id === tempId ? { ...m, content: snap } : m))
              );
            }
          } catch {
            buffer = line + "\n" + buffer;
            break;
          }
        }
      }

      // Now split by --- into multiple bubbles
      const MOOD_MARKER = "[MOOD_CHECK]";
      const parts = assistantContent.split(/\n?---\n?/).map(p => p.trim()).filter(Boolean);

      // Remove the streaming temp message
      setMessages((prev) => prev.filter((m) => m.id !== tempId));

      if (parts.length > 1) {
        // Stagger each bubble with a typing indicator pause
        for (let i = 0; i < parts.length; i++) {
          if (i > 0) {
            // Show typing indicator before subsequent bubbles
            setIsTyping(true);
            await new Promise((r) => setTimeout(r, 1500 + Math.random() * 1000));
          }
          setIsTyping(false);
          let partText = parts[i];
          const hasMoodCheck = partText.includes(MOOD_MARKER);
          partText = partText.replace(MOOD_MARKER, "").trim();
          if (!partText) continue;
          setMessages((prev) => [
            ...prev,
            { id: crypto.randomUUID(), content: partText, isUser: false, timestamp: new Date(), moodCheck: hasMoodCheck },
          ]);
        }
      } else {
        let text = assistantContent.trim();
        const hasMoodCheck = text.includes(MOOD_MARKER);
        text = text.replace(MOOD_MARKER, "").trim();
        setMessages((prev) => [
          ...prev,
          { id: crypto.randomUUID(), content: text, isUser: false, timestamp: new Date(), moodCheck: hasMoodCheck },
        ]);
      }

      // Store cleaned content (without --- and mood markers) so the model doesn't learn the split pattern
      const cleanedContent = assistantContent.replace(/\n?---\n?/g, ' ').replace(MOOD_MARKER, '').trim();
      historyRef.current.push({ role: "assistant", content: cleanedContent });

      // Encrypt and save the transcript (client-side encryption only)
      await persist();
    } catch (e) {
      console.error("mindful-chat stream error:", e);
      toast({ title: "Error", description: "Failed to connect. Please try again.", variant: "destructive" });
    } finally {
      setIsTyping(false);
      busyRef.current = false;
    }
  }, [toast, persist]);

  const startNewConversation = useCallback(() => {
    const welcome = { ...WELCOME_MESSAGE, id: crypto.randomUUID(), timestamp: new Date() };
    setMessages([welcome]);
    messagesRef.current = [welcome];
    historyRef.current = [];
    conversationIdRef.current = null;
    setConversationId(null);
  }, []);

  const openConversation = useCallback(async (id: string) => {
    const p = persistenceRef.current;
    if (!p?.enabled) return;
    const stored = (await p.loadConversation(id)) as StoredConversation | null;
    if (!stored) {
      toast({ title: "Could not open", description: "This conversation could not be decrypted.", variant: "destructive" });
      return;
    }
    const restored: ChatMessage[] = stored.messages.map((m) => ({
      id: m.id,
      content: m.content,
      isUser: m.isUser,
      timestamp: new Date(m.timestamp),
      moodCheck: m.moodCheck,
    }));
    setMessages(restored);
    messagesRef.current = restored;
    historyRef.current = stored.history ?? [];
    conversationIdRef.current = id;
    setConversationId(id);
  }, [toast]);

  return {
    messages,
    isTyping,
    sendMessage,
    conversationId,
    startNewConversation,
    openConversation,
    reset: startNewConversation,
  };
}
